import React, { useState } from 'react';
import { CalendarIcon } from 'lucide-react';
import type { DateRange } from 'react-day-picker';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import {
  decodeDateRangeFilter,
  encodeDateRangeFilter,
} from './dateRangeFilterUtils';

interface DateRangeFilterInputProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

const DateRangeFilterInput: React.FC<DateRangeFilterInputProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const selected = decodeDateRangeFilter(value);

  const label = (() => {
    if (!selected?.from) return 'Select date range...';
    const start = format(selected.from, 'MMM d, yyyy');
    const end = selected.to ?? selected.from;
    if (format(selected.from, 'yyyy-MM-dd') === format(end, 'yyyy-MM-dd')) {
      return start;
    }
    return `${start} – ${format(end, 'MMM d, yyyy')}`;
  })();

  const handleSelect = (range: DateRange | undefined) => {
    onChange(encodeDateRangeFilter(range));
    if (range?.from && range?.to) {
      setOpen(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            'h-9 w-full justify-start text-left font-normal',
            !selected?.from && 'text-muted-foreground',
          )}
          disabled={disabled}
        >
          <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
          <span className="truncate">{label}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="range"
          selected={selected}
          onSelect={handleSelect}
          numberOfMonths={2}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
};

export default DateRangeFilterInput;
